import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Lock, User, ArrowRight, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { signInWithGoogle, auth } from './firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import Button from './components/ui/button';
import Input from './components/ui/input';

export default function AuthModal({ isOpen, onClose, initialMode }) {
  const [authMode, setAuthMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setAuthMode(initialMode);
  }, [initialMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      if (authMode === 'signup') {
        if (password !== confirmPassword) {
          setError("Passwords don't match");
          return;
        }
        await createUserWithEmailAndPassword(auth, email, password);
        // Update user profile with first and last name
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
      onClose();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      onClose();
    } catch (error) {
      setError(error.message);
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const toggleAuthMode = () => {
    setAuthMode(authMode === 'signup' ? 'signin' : 'signup');
    setError(null);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center z-50 p-4 overflow-y-auto"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="bg-gray-800 p-6 sm:p-8 rounded-2xl w-full max-w-4xl mx-auto shadow-2xl flex flex-col lg:flex-row"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Form section */}
            <div className="flex-1 lg:pr-8 mb-8 lg:mb-0">
              <div className="flex justify-between items-center mb-6 sm:mb-8">
                <h2 className="text-2xl sm:text-4xl font-bold text-white bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-orange-500">
                  {authMode === 'signup' ? 'Create Your Account' : 'Welcome Back'}
                </h2>
                <Button variant="ghost" onClick={onClose} className="text-gray-400 hover:text-white">
                  <X className="h-6 w-6" />
                </Button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                {authMode === 'signup' && (
                  <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                    <Input
                      type="text"
                      placeholder="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      className="bg-gray-700 border-2 border-gray-600 text-white placeholder-gray-400 focus:border-yellow-400 focus:ring-yellow-400 rounded-xl py-2 sm:py-3 text-base sm:text-lg w-full"
                      icon={<User className="text-gray-400" />}
                    />
                    <Input
                      type="text"
                      placeholder="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      className="bg-gray-700 border-2 border-gray-600 text-white placeholder-gray-400 focus:border-yellow-400 focus:ring-yellow-400 rounded-xl py-2 sm:py-3 text-base sm:text-lg w-full"
                      icon={<User className="text-gray-400" />}
                    />
                  </div>
                )}
                <Input
                  type="email"
                  placeholder="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-gray-700 border-2 border-gray-600 text-white placeholder-gray-400 focus:border-yellow-400 focus:ring-yellow-400 rounded-xl py-2 sm:py-3 text-base sm:text-lg w-full"
                  icon={<Mail className="text-gray-400" />}
                />
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-gray-700 border-2 border-gray-600 text-white placeholder-gray-400 focus:border-yellow-400 focus:ring-yellow-400 rounded-xl py-2 sm:py-3 text-base sm:text-lg w-full pr-10"
                    icon={<Lock className="text-gray-400" />}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {authMode === 'signup' && (
                  <Input
                    type="password"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="bg-gray-700 border-2 border-gray-600 text-white placeholder-gray-400 focus:border-yellow-400 focus:ring-yellow-400 rounded-xl py-2 sm:py-3 text-base sm:text-lg w-full"
                    icon={<Lock className="text-gray-400" />}
                  />
                )}
                <Button type="submit" className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 hover:from-yellow-300 hover:to-orange-400 py-2 sm:py-3 rounded-xl text-base sm:text-lg font-semibold transition-all duration-300 flex items-center justify-center">
                  {authMode === 'signup' ? 'Create Account' : 'Sign In'}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </form>
              {error && <p className="mt-4 text-red-400 text-sm">{error}</p>}
              <p className="mt-6 text-center text-sm text-gray-400">
                {authMode === 'signup' ? 'Already have an account?' : "Don't have an account?"}{' '}
                <button
                  onClick={toggleAuthMode}
                  className="text-yellow-400 hover:text-yellow-300 font-semibold"
                >
                  {authMode === 'signup' ? 'Sign In' : 'Sign Up'}
                </button>
              </p>
            </div>

            {/* Info section */}
            <div className="flex-1 lg:border-l lg:border-gray-700 lg:pl-8 flex flex-col justify-between">
              <div>
                <h3 className="text-xl sm:text-2xl font-semibold text-white mb-4">Quick Access</h3>
                <Button 
                  onClick={handleGoogleSignIn} 
                  className="w-full bg-white hover:bg-gray-100 flex items-center justify-center space-x-2 py-2 sm:py-3 rounded-xl text-base sm:text-lg font-semibold transition-all duration-300 mb-4"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                    <path fill="none" d="M1 1h22v22H1z" />
                  </svg>
                  <span className="text-gray-900">Continue with Google</span>
                </Button>
              </div>
              <div className="mt-8">
                <h4 className="text-lg sm:text-xl font-semibold text-white mb-4">Why choose News Nine?</h4>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>Unbiased reporting</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>Real-time updates</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>Personalized news feed</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span>Expert analysis</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

AuthModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialMode: PropTypes.oneOf(['signup', 'signin']).isRequired,
};